import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import useScrollToTop from "../../hooks/useScollToTop";
import ScrollIndicator from "../Home/ScrollIndicator";

import CastingHeader from "./components/CastingHeader";
import MatsSidebar from "./components/MatsSidebar";
import MatEditor from "./components/MatEditor";
import MessagesPanel from "./components/MessagesPanel";
import SurfaceSetupCard from "./components/SurfaceSetupCard";

import {
  addEventMat,
  createEventCastMessage,
  deleteEventCastMessage,
  deleteEventMat,
  getEventCastBundle,
  initializeEventMats,
  toggleEventCastMessageActive,
  toggleEventMatOpen,
  updateEventCastMessage,
  upsertEventCastState,
  upsertEventMatWithSlots,
} from "../../services/eventCastServices";

import {
  deleteMatStream,
  toggleMatStream,
  upsertMatStream,
} from "../../services/eventLiveServices";

const FALLBACK_EVENT = (eventId) => {
  try {
    const raw = localStorage.getItem(
      `casting_event_${eventId}`
    );

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

function extractYouTubeId(url = "") {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.replace(
      "www.",
      ""
    );

    if (host === "youtu.be") {
      return parsedUrl.pathname
        .split("/")
        .filter(Boolean)[0];
    }

    if (parsedUrl.pathname === "/watch") {
      return parsedUrl.searchParams.get("v");
    }

    for (const prefix of [
      "/live/",
      "/embed/",
      "/shorts/",
    ]) {
      if (parsedUrl.pathname.startsWith(prefix)) {
        return parsedUrl.pathname
          .replace(prefix, "")
          .split("/")[0];
      }
    }
  } catch {
    const match = String(url).match(
      /(?:v=|\/live\/|youtu\.be\/|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
    );

    return match?.[1] || null;
  }

  return null;
}

function detectProvider(url = "") {
  const normalizedUrl = String(
    url || ""
  ).toLowerCase();

  if (
    normalizedUrl.includes("youtube.com") ||
    normalizedUrl.includes("youtu.be")
  ) {
    return "YOUTUBE";
  }

  if (normalizedUrl.includes("twitch.tv")) {
    return "TWITCH";
  }

  if (normalizedUrl.includes("vimeo.com")) {
    return "VIMEO";
  }

  return "OTHER";
}

function buildYoutubeEmbed(url = "") {
  const videoId = extractYouTubeId(url);

  if (!videoId) return "";

  return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`;
}

const createEmptySlot = (slotIndex) => ({
  slot_index: slotIndex,
  label: "",
  note: "",
  is_active: true,
});

const initMatForm = (
  selectedMat = 1,
  mat = null
) => {
  const surfaceType =
    mat?.surface_type || "MAT";

  const slotsSource = Array.isArray(mat?.slots)
    ? mat.slots
    : Array.isArray(mat?.Slots)
    ? mat.Slots
    : [];

  const slots =
    surfaceType === "MAT"
      ? slotsSource.length > 0
        ? [...slotsSource]
            .sort(
              (a, b) =>
                Number(a.slot_index) -
                Number(b.slot_index)
            )
            .map((slot, index) => ({
              slot_index: index + 1,
              label: String(slot?.label || ""),
              note: String(slot?.note || ""),
              is_active:
                slot?.is_active !== undefined
                  ? Boolean(slot.is_active)
                  : true,
            }))
        : [1, 2, 3].map(createEmptySlot)
      : [];

  const stream =
    mat?.stream || mat?.Stream || null;

  return {
    surface_type: surfaceType,

    label:
      mat?.label || `Tapis ${selectedMat}`,

    is_open:
      mat?.is_open !== undefined
        ? Boolean(mat.is_open)
        : true,

    sort_order:
      mat?.sort_order ?? Number(selectedMat),

    slots,

    stream_id: stream?.id || null,
    stream_provider:
      stream?.provider || "YOUTUBE",
    stream_url: stream?.stream_url || "",
    embed_url: stream?.embed_url || "",
    stream_title: stream?.title || "",

    stream_is_live:
      stream?.is_live !== undefined
        ? Boolean(stream.is_live)
        : true,
  };
};

export default function CastingPage() {
  useScrollToTop();

  const { eventId } = useParams();

  const [loading, setLoading] = useState(true);
  const [savingMat, setSavingMat] =
    useState(false);
  const [savingStream, setSavingStream] =
    useState(false);
  const [initializingMats, setInitializingMats] =
    useState(false);
  const [addingMat, setAddingMat] =
    useState(false);
  const [deletingMat, setDeletingMat] =
    useState(false);

  const [event, setEvent] = useState(() =>
    FALLBACK_EVENT(eventId)
  );

  const [mats, setMats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [castState, setCastState] =
    useState(null);

  const [panel, setPanel] = useState("mats");
  const [selectedMat, setSelectedMat] =
    useState(1);

  const [matForm, setMatForm] = useState(() =>
    initMatForm(1, null)
  );

  const selectedMatData = useMemo(
    () =>
      mats.find(
        (mat) =>
          Number(mat.mat_number) ===
          Number(selectedMat)
      ) || null,
    [mats, selectedMat]
  );

  const fetchBundle = useCallback(
    async ({ silent = false } = {}) => {
      if (!eventId) return;

      if (!silent) {
        setLoading(true);
      }

      try {
        const data =
          await getEventCastBundle(eventId);

        const loadedMats = Array.isArray(
          data?.mats
        )
          ? data.mats
          : [];

        setEvent(
          data?.event || FALLBACK_EVENT(eventId)
        );

        setMats(loadedMats);

        setMessages(
          Array.isArray(data?.messages)
            ? data.messages
            : []
        );

        setCastState(data?.state || null);

        setSelectedMat((currentSelected) => {
          const currentExists = loadedMats.some(
            (mat) =>
              Number(mat.mat_number) ===
              Number(currentSelected)
          );

          if (currentExists) {
            return currentSelected;
          }

          return loadedMats.length > 0
            ? Number(loadedMats[0].mat_number)
            : 1;
        });
      } catch (error) {
        console.error(error);

        toast.error(
          error?.response?.data?.message ||
            "Erreur lors du chargement des données."
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [eventId]
  );

  useEffect(() => {
    fetchBundle();
  }, [fetchBundle]);

  useEffect(() => {
    setMatForm(
      initMatForm(
        selectedMat,
        selectedMatData
      )
    );
  }, [
    selectedMat,
    selectedMatData?.id,
    selectedMatData?.updated_at,
    selectedMatData?.stream?.id,
    selectedMatData?.stream?.updated_at,
  ]);

  const initializeMats = async (count) => {
    setInitializingMats(true);

    try {
      const result =
        await initializeEventMats(
          eventId,
          count
        );

      toast.success(
        result?.message ||
          "Les surfaces ont été créées."
      );

      setSelectedMat(1);
      await fetchBundle();
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de créer les surfaces."
      );
    } finally {
      setInitializingMats(false);
    }
  };

  const addMat = async () => {
    if (addingMat) return;

    setAddingMat(true);

    try {
      const result = await addEventMat(
        eventId,
        {
          surface_type: "MAT",
          is_open: true,
        }
      );

      const newMat = result?.mat;

      if (!newMat) {
        throw new Error(
          "Surface non retournée."
        );
      }

      setMats((currentMats) =>
        [...currentMats, newMat].sort(
          (a, b) =>
            (Number(a.sort_order) ||
              Number(a.mat_number)) -
            (Number(b.sort_order) ||
              Number(b.mat_number))
        )
      );

      setSelectedMat(
        Number(newMat.mat_number)
      );

      setMatForm(
        initMatForm(
          Number(newMat.mat_number),
          newMat
        )
      );

      toast.success(
        result?.message || "Surface ajoutée."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible d’ajouter une surface."
      );
    } finally {
      setAddingMat(false);
    }
  };

  const removeMat = async () => {
    if (
      !selectedMatData ||
      deletingMat
    ) {
      return;
    }

    const surfaceLabel =
      selectedMatData.label ||
      `Tapis ${selectedMat}`;

    const confirmed = window.confirm(
      `Supprimer définitivement « ${surfaceLabel} » ?\n\n` +
        "Ses catégories et son éventuel live seront également supprimés."
    );

    if (!confirmed) return;

    setDeletingMat(true);

    try {
      const sortedMats = [...mats].sort(
        (a, b) =>
          (Number(a.sort_order) ||
            Number(a.mat_number)) -
          (Number(b.sort_order) ||
            Number(b.mat_number))
      );

      const deletedIndex =
        sortedMats.findIndex(
          (mat) =>
            Number(mat.mat_number) ===
            Number(selectedMat)
        );

      const result = await deleteEventMat(
        eventId,
        selectedMat
      );

      const remainingMats =
        sortedMats.filter(
          (mat) =>
            Number(mat.mat_number) !==
            Number(selectedMat)
        );

      setMats(remainingMats);

      setMessages((currentMessages) =>
        currentMessages.map((message) =>
          Number(
            message.target_mat_number
          ) === Number(selectedMat)
            ? {
                ...message,
                target_mat_number: null,
              }
            : message
        )
      );

      if (remainingMats.length > 0) {
        const nextIndex = Math.min(
          Math.max(deletedIndex, 0),
          remainingMats.length - 1
        );

        setSelectedMat(
          Number(
            remainingMats[nextIndex]
              .mat_number
          )
        );
      } else {
        setSelectedMat(1);
        setMatForm(initMatForm(1, null));
      }

      toast.success(
        result?.message ||
          "Surface supprimée."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de supprimer la surface."
      );
    } finally {
      setDeletingMat(false);
    }
  };

  const saveMat = async () => {
    const isCompetitionMat =
      (matForm.surface_type || "MAT") ===
      "MAT";

    const slots = isCompetitionMat
      ? (matForm.slots || [])
          .map((slot, index) => ({
            slot_index: index + 1,

            label: String(
              slot.label || ""
            ).trim(),

            note: String(
              slot.note || ""
            ).trim(),

            is_active:
              slot.is_active !== undefined
                ? Boolean(slot.is_active)
                : true,
          }))
          .filter((slot) => slot.label)
      : [];

    setSavingMat(true);

    try {
      const saved =
        await upsertEventMatWithSlots(
          eventId,
          selectedMat,
          {
            surface_type:
              matForm.surface_type || "MAT",

            label: String(
              matForm.label || ""
            ).trim(),

            is_open: Boolean(
              matForm.is_open
            ),

            sort_order: Number(
              matForm.sort_order ||
                selectedMat
            ),

            slots,
          }
        );

      const oldMat = mats.find(
        (mat) =>
          Number(mat.mat_number) ===
          Number(selectedMat)
      );

      const savedSlots = Array.isArray(
        saved?.slots
      )
        ? saved.slots
        : Array.isArray(saved?.Slots)
        ? saved.Slots
        : [];

      const normalizedMat = {
        ...saved,

        mat_number: Number(
          saved?.mat_number ?? selectedMat
        ),

        surface_type:
          saved?.surface_type ||
          matForm.surface_type ||
          "MAT",

        Slots: savedSlots,
        slots: savedSlots,

        stream:
          saved?.stream ||
          saved?.Stream ||
          oldMat?.stream ||
          null,
      };

      setMats((currentMats) => {
        const index = currentMats.findIndex(
          (mat) =>
            Number(mat.mat_number) ===
            Number(selectedMat)
        );

        const nextMats = [
          ...currentMats,
        ];

        if (index >= 0) {
          nextMats[index] = normalizedMat;
        } else {
          nextMats.push(normalizedMat);
        }

        return nextMats.sort(
          (a, b) =>
            (Number(a.sort_order) ||
              Number(a.mat_number)) -
            (Number(b.sort_order) ||
              Number(b.mat_number))
        );
      });

      setMatForm(
        initMatForm(
          selectedMat,
          normalizedMat
        )
      );

      toast.success("Surface enregistrée.");
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de l’enregistrement."
      );
    } finally {
      setSavingMat(false);
    }
  };

  const toggleMat = async () => {
    try {
      const result =
        await toggleEventMatOpen(
          eventId,
          selectedMat
        );

      setMatForm((currentForm) => ({
        ...currentForm,
        is_open: Boolean(
          result?.is_open
        ),
      }));

      setMats((currentMats) =>
        currentMats.map((mat) =>
          Number(mat.mat_number) ===
          Number(selectedMat)
            ? {
                ...mat,
                is_open: Boolean(
                  result?.is_open
                ),
              }
            : mat
        )
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de changer l’état."
      );
    }
  };

  const saveStream = async () => {
    const streamUrl = String(
      matForm.stream_url || ""
    ).trim();

    if (!streamUrl) {
      toast.error(
        "Renseigne un lien vidéo."
      );
      return;
    }

    const provider =
      matForm.stream_provider ||
      detectProvider(streamUrl);

    const embedUrl =
      provider === "YOUTUBE"
        ? buildYoutubeEmbed(streamUrl)
        : String(
            matForm.embed_url || ""
          ).trim();

    setSavingStream(true);

    try {
      const result = await upsertMatStream(
        eventId,
        selectedMat,
        {
          provider,
          stream_url: streamUrl,
          embed_url: embedUrl || null,

          title:
            String(
              matForm.stream_title || ""
            ).trim() || null,

          is_live: Boolean(
            matForm.stream_is_live
          ),
        }
      );

      const stream = result?.stream || null;

      setMats((currentMats) =>
        currentMats.map((mat) =>
          Number(mat.mat_number) ===
          Number(selectedMat)
            ? {
                ...mat,
                stream,
              }
            : mat
        )
      );

      setMatForm((currentForm) => ({
        ...currentForm,
        stream_id: stream?.id || null,
        stream_provider:
          stream?.provider || provider,
        stream_url:
          stream?.stream_url || streamUrl,
        embed_url:
          stream?.embed_url || embedUrl,
        stream_title:
          stream?.title || "",
        stream_is_live:
          stream?.is_live !== undefined
            ? Boolean(stream.is_live)
            : true,
      }));

      toast.success(
        "Live vidéo enregistré."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible d’enregistrer le live."
      );
    } finally {
      setSavingStream(false);
    }
  };

  const toggleStream = async () => {
    try {
      const result = await toggleMatStream(
        eventId,
        selectedMat
      );

      setMatForm((currentForm) => ({
        ...currentForm,
        stream_is_live: Boolean(
          result?.is_live
        ),
      }));

      setMats((currentMats) =>
        currentMats.map((mat) =>
          Number(mat.mat_number) ===
          Number(selectedMat)
            ? {
                ...mat,
                stream: mat.stream
                  ? {
                      ...mat.stream,
                      is_live: Boolean(
                        result?.is_live
                      ),
                    }
                  : null,
              }
            : mat
        )
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de modifier le live."
      );
    }
  };

  const removeStream = async () => {
    const confirmed = window.confirm(
      "Supprimer le live vidéo de cette surface ?"
    );

    if (!confirmed) return;

    try {
      await deleteMatStream(
        eventId,
        selectedMat
      );

      setMats((currentMats) =>
        currentMats.map((mat) =>
          Number(mat.mat_number) ===
          Number(selectedMat)
            ? {
                ...mat,
                stream: null,
              }
            : mat
        )
      );

      setMatForm((currentForm) => ({
        ...currentForm,
        stream_id: null,
        stream_provider: "YOUTUBE",
        stream_url: "",
        embed_url: "",
        stream_title: "",
        stream_is_live: true,
      }));

      toast.success("Live supprimé.");
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de supprimer le live."
      );
    }
  };

  const setMode = async (mode) => {
    try {
      const updated =
        await upsertEventCastState(
          eventId,
          {
            mode,

            active_message_id:
              mode === "MESSAGE"
                ? castState?.active_message_id ??
                  null
                : null,
          }
        );

      setCastState(updated);
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Impossible de modifier l’écran."
      );
    }
  };

  const openScreen = () => {
    window.open(
      `/casting-screen/${eventId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (!eventId) {
    return (
      <div className="p-6 text-rose-600">
        eventId manquant dans l’URL.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto w-full max-w-[1500px] px-3 pb-12 pt-[130px] sm:px-6 md:pt-[150px]">
        <ScrollIndicator />

        <CastingHeader
          event={event}
          eventId={eventId}
          panel={panel}
          setPanel={setPanel}
          castState={castState}
          onRefresh={() => fetchBundle()}
          onOpenScreen={openScreen}
          onSetMode={setMode}
        />

        {panel === "mats" ? (
          <div className="mt-5">
            {loading && mats.length === 0 ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center dark:border-white/10 dark:bg-slate-900">
                Chargement…
              </div>
            ) : mats.length === 0 ? (
              <SurfaceSetupCard
                loading={initializingMats}
                onInitialize={initializeMats}
              />
            ) : (
              <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
                <MatsSidebar
                  mats={mats}
                  selectedMat={selectedMat}
                  onSelectMat={setSelectedMat}
                  currentLabel={
                    matForm.label ||
                    `Tapis ${selectedMat}`
                  }
                  isOpen={Boolean(
                    matForm.is_open
                  )}
                  onToggleCurrentMat={toggleMat}
                  onAddSurface={addMat}
                  addingSurface={addingMat}
                  onDeleteSurface={removeMat}
                  deletingSurface={deletingMat}
                />

                <MatEditor
                  loading={loading}
                  saving={savingMat}
                  savingStream={savingStream}
                  selectedMat={selectedMat}
                  form={matForm}
                  setForm={setMatForm}
                  onSave={saveMat}
                  onSaveStream={saveStream}
                  onToggleStream={toggleStream}
                  onDeleteStream={removeStream}
                  onAutoEmbed={() => {
                    setMatForm(
                      (currentForm) => {
                        const url = String(
                          currentForm.stream_url ||
                            ""
                        ).trim();

                        const provider =
                          detectProvider(url);

                        return {
                          ...currentForm,
                          stream_provider:
                            provider,
                          embed_url:
                            provider ===
                            "YOUTUBE"
                              ? buildYoutubeEmbed(
                                  url
                                )
                              : currentForm.embed_url,
                        };
                      }
                    );
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="mt-5">
            <MessagesPanel
              eventId={eventId}
              mats={mats}
              messages={messages}
              setMessages={setMessages}
              castState={castState}
              setCastState={setCastState}
              services={{
                createEventCastMessage,
                updateEventCastMessage,
                toggleEventCastMessageActive,
                deleteEventCastMessage,
                upsertEventCastState,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}